import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export type ProgressBarTone = "primary" | "success" | "warning" | "danger" | "info";
export type ProgressBarSize = "sm" | "md" | "lg";

export type ProgressBarProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
  value?: number;
  max?: number;
  indeterminate?: boolean;
  tone?: ProgressBarTone;
  size?: ProgressBarSize;
  label?: ReactNode;
  showValue?: boolean;
  formatValue?: (percent: number, value: number, max: number) => ReactNode;
  trackClassName?: string;
  barClassName?: string;
};

const trackSizeClassName: Record<ProgressBarSize, string> = {
  sm: "bfrs-h-1.5",
  md: "bfrs-h-2",
  lg: "bfrs-h-3"
};

const toneClassName: Record<ProgressBarTone, string> = {
  primary: "bfrs-bg-primary",
  success: "bfrs-bg-success",
  warning: "bfrs-bg-warning",
  danger: "bfrs-bg-danger",
  info: "bfrs-bg-info"
};

export const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      className,
      trackClassName,
      barClassName,
      value = 0,
      max = 100,
      indeterminate = false,
      tone = "primary",
      size = "md",
      label,
      showValue = false,
      formatValue,
      "aria-label": ariaLabel,
      ...props
    },
    ref
  ) => {
    const safeMax = max > 0 ? max : 100;
    const clampedValue = Math.min(Math.max(value, 0), safeMax);
    const percent = Math.round((clampedValue / safeMax) * 100);
    const valueText = formatValue ? formatValue(percent, clampedValue, safeMax) : `${percent}%`;
    const resolvedAriaLabel = ariaLabel ?? (typeof label === "string" ? label : undefined);
    const showHeader = label !== undefined || (showValue && !indeterminate);

    return (
      <div ref={ref} className={cn("bfrs-w-full", className)} {...props}>
        {showHeader && (
          <div className="bfrs-mb-1.5 bfrs-flex bfrs-items-center bfrs-justify-between bfrs-gap-3">
            {label !== undefined ? (
              <span className="bfrs-text-[12px] bfrs-font-semibold bfrs-text-text-secondary">{label}</span>
            ) : (
              <span />
            )}
            {showValue && !indeterminate && (
              <span className="bfrs-shrink-0 bfrs-text-xs bfrs-font-semibold bfrs-text-text-primary">{valueText}</span>
            )}
          </div>
        )}
        <div
          role="progressbar"
          aria-label={resolvedAriaLabel}
          aria-valuemin={indeterminate ? undefined : 0}
          aria-valuemax={indeterminate ? undefined : 100}
          aria-valuenow={indeterminate ? undefined : percent}
          aria-valuetext={indeterminate ? undefined : typeof valueText === "string" ? valueText : `${percent}%`}
          className={cn("bfrs-relative bfrs-w-full bfrs-overflow-hidden bfrs-rounded-pill bfrs-bg-surface-raised", trackSizeClassName[size], trackClassName)}
        >
          {indeterminate ? (
            <div className={cn("bfrs-absolute bfrs-inset-y-0 bfrs-left-0 bfrs-w-2/5 bfrs-rounded-pill bfrs-animate-progress-indeterminate", toneClassName[tone], barClassName)} />
          ) : (
            <div
              className={cn("bfrs-h-full bfrs-rounded-pill bfrs-transition-[width] bfrs-duration-300 bfrs-ease-out", toneClassName[tone], barClassName)}
              style={{ width: `${percent}%` }}
            />
          )}
        </div>
      </div>
    );
  }
);

ProgressBar.displayName = "ProgressBar";
